import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import {
  type Expr,
  type SolveStep,
  Polynomial,
  Fraction,
  parseEquation,
  solveEquation,
  num,
  variable,
  add,
  mul,
  pow,
  func,
  sigma,
  canonicalise,
  format,
} from '../algebra';
import type { Prng } from '@learnforge/demo-math/numeric';
import type { AlgebraContext, AlgebraKey } from '../types';
import { createBalanceScale, type Tile, type BalanceOp } from '../render/balance-scale';
import { createSvgEl } from '@learnforge/demo-render';
import { mountActionBar } from '../action-bar';
import { EQUATION_PRESETS } from '../data/equation-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

export interface BalanceEquation {
  lhs: Expr;
  rhs: Expr;
}

/**
 * Parse a string equation input into structured lhs/rhs.
 * Wraps parseEquation and throws on invalid input.
 */
export function parseEquationInput(src: string): BalanceEquation {
  const eq = parseEquation(src);
  return { lhs: eq.lhs, rhs: eq.rhs };
}

/**
 * Apply an operation to both sides of an equation.
 * Returns a new equation with both sides transformed.
 */
export function applyOperationToBoth(eq: BalanceEquation, op: BalanceOp): BalanceEquation {
  const { lhs, rhs } = eq;

  switch (op.kind) {
    case 'add': {
      return {
        lhs: canonicalise(add(lhs, op.value)),
        rhs: canonicalise(add(rhs, op.value)),
      };
    }
    case 'sub': {
      return {
        lhs: canonicalise(add(lhs, mul(num(-1), op.value))),
        rhs: canonicalise(add(rhs, mul(num(-1), op.value))),
      };
    }
    case 'mul': {
      return {
        lhs: canonicalise(mul(lhs, op.value)),
        rhs: canonicalise(mul(rhs, op.value)),
      };
    }
    case 'div': {
      // Division by a numeric divisor multiplies by its reciprocal directly
      // (a clean `num` node), so canonicalise's numeric folding collapses the
      // whole factor away — e.g. `2x ÷ 2` reduces to `x`, not `2x · 2⁻¹`
      // (which round-trips through format()/parseEquationInput ambiguously).
      // Division by a symbolic value falls back to raising to the -1 power.
      if (op.value.kind === 'num') {
        const reciprocal = num(op.value.value.reciprocal());
        return {
          lhs: canonicalise(mul(lhs, reciprocal)),
          rhs: canonicalise(mul(rhs, reciprocal)),
        };
      }
      return {
        lhs: canonicalise(mul(lhs, pow(op.value, num(-1)))),
        rhs: canonicalise(mul(rhs, pow(op.value, num(-1)))),
      };
    }
    case 'square': {
      return {
        lhs: canonicalise(pow(lhs, num(2))),
        rhs: canonicalise(pow(rhs, num(2))),
      };
    }
  }
}

/**
 * Check if an expression contains a specific variable.
 */
function containsVar(expr: Expr, varName: string): boolean {
  switch (expr.kind) {
    case 'num':
      return false;
    case 'var':
      return expr.name === varName;
    case 'add':
    case 'mul':
      return expr.children.some((c) => containsVar(c, varName));
    case 'pow':
      return containsVar(expr.base, varName) || containsVar(expr.exp, varName);
    case 'func':
      return expr.args.some((a) => containsVar(a, varName));
    case 'sigma':
      return (
        containsVar(expr.from, varName) ||
        containsVar(expr.to, varName) ||
        containsVar(expr.body, varName)
      );
  }
}

/**
 * Check if an expression contains any variable.
 */
function hasAnyVariable(expr: Expr): boolean {
  switch (expr.kind) {
    case 'num':
      return false;
    case 'var':
      return true;
    case 'add':
    case 'mul':
      return expr.children.some((c) => hasAnyVariable(c));
    case 'pow':
      return hasAnyVariable(expr.base) || hasAnyVariable(expr.exp);
    case 'func':
      return expr.args.some((a) => hasAnyVariable(a));
    case 'sigma':
      return hasAnyVariable(expr.from) || hasAnyVariable(expr.to) || hasAnyVariable(expr.body);
  }
}

/**
 * Check if an equation is solved: lhs is a single variable and rhs is numeric.
 */
export function isSolved(eq: BalanceEquation, varName: string): boolean {
  // LHS must be the isolated variable
  if (eq.lhs.kind !== 'var' || eq.lhs.name !== varName) {
    return false;
  }

  // RHS must be numeric (no variables)
  return !containsVar(eq.rhs, varName) && !hasAnyVariable(eq.rhs);
}

/**
 * Solve a linear equation and return solutions + steps.
 */
export function solveLinear(
  eq: BalanceEquation,
  varName: string,
): {
  solutions: Expr[];
  steps: SolveStep[];
} {
  const result = solveEquation(eq.lhs, eq.rhs, varName);
  return {
    solutions: result.solutions,
    steps: result.steps,
  };
}

/**
 * Fold any purely-numeric `pow(num, num)` subtree into a single `num` node.
 * `canonicalise` never evaluates a `pow` node — even a fully numeric one —
 * it only folds literal `num` children of a surrounding `add`/`mul`. But
 * division (both this module's own div-by-symbolic fallback below, and the
 * parser's own "/" syntax when re-parsing a formatted string) builds a
 * `pow(divisor, -1)` node, which then sits un-evaluated forever unless
 * something folds it — breaking `Polynomial.fromExpr` (which refuses a
 * negative exponent) wherever a side still carries one. Apply this before
 * canonicalise wherever a side is about to be tiled, string-formatted, or
 * checked for a divisor candidate.
 */
function foldNumericPowers(e: Expr): Expr {
  switch (e.kind) {
    case 'num':
    case 'var':
      return e;
    case 'add':
      return add(...e.children.map(foldNumericPowers));
    case 'mul':
      return mul(...e.children.map(foldNumericPowers));
    case 'pow': {
      const base = foldNumericPowers(e.base);
      const exp = foldNumericPowers(e.exp);
      if (base.kind === 'num' && exp.kind === 'num' && exp.value.isInteger()) {
        // Guard 0^(negative) — leave a genuinely undefined power unevaluated
        // rather than let Fraction.reciprocal() throw on a degenerate input.
        if (exp.value.num < 0 && base.value.num === 0) return pow(base, exp);
        return num(base.value.pow(exp.value.num));
      }
      return pow(base, exp);
    }
    case 'func':
      return func(e.name, ...e.args.map(foldNumericPowers));
    case 'sigma':
      return sigma(
        e.index,
        foldNumericPowers(e.from),
        foldNumericPowers(e.to),
        foldNumericPowers(e.body),
      );
  }
}

/**
 * Format one side of an equation as a display string. Routes through
 * `Polynomial` when the side is polynomial-representable in `varName`, so
 * repeated single-unit ± ops (as the op-button row applies them) round-trip
 * to a fully collected, minimal form — e.g. two separate "− x" clicks plus a
 * "3(x − 1)" preset's un-distributed product both resolve to one clean `x`
 * term. Plain `canonicalise`+`format` never collects like terms or
 * distributes a product/fraction over a sum, so without this a side can get
 * permanently stuck rendering an equivalent-but-unreduced expression (and
 * `isSolved`'s raw `lhs.kind === 'var'` check would never fire). Falls back
 * to `format(canonicalise(...))` for non-polynomial sides (e.g. `sqrt(x)`),
 * which this experiment's own buttons never produce, but a hand-typed
 * equation still can.
 *
 * Every render of this experiment — the ascii round-trip in
 * `equationToString`, and the headline/solved-banner display text in
 * `update()` (unicode superscripts on) — goes through this single function,
 * so a hand-typed division (e.g. `x/3 + 2 = 5`, parsed as
 * `mul(x, pow(3, -1))`) always folds/collects the same way instead of ever
 * falling back to a raw `format(eq.lhs, ...)` call on the un-folded parse
 * (which glues the unevaluated `pow` factor directly against the next term,
 * e.g. `x1/3^1 + 2 = 5`). Exported (rather than module-private) so the
 * pow-folding/Polynomial-routing behaviour can be pinned directly by a unit
 * test; not part of this experiment's documented public interface.
 */
export function formatSide(
  side: Expr,
  varName: string,
  options?: { useUnicodeSuperscripts?: boolean },
): string {
  const folded = foldNumericPowers(side);
  const poly = Polynomial.fromExpr(canonicalise(folded), varName);
  if (poly) return format(poly.toExpr(), options);
  return format(canonicalise(folded), options);
}

/**
 * Map one side of an equation to balance-scale tiles. Only integer-coefficient
 * linear sides (degree ≤ 1, small whole-number coefficients) are tileable —
 * everything else (fractions, higher degree, oversized coefficients) returns
 * null so the caller can fall back to a "not tileable" message.
 */
export function sideToTiles(side: Expr, varName: string): Tile[] | null {
  const poly = Polynomial.fromExpr(canonicalise(foldNumericPowers(side)), varName);
  if (!poly || poly.degree > 1) return null;
  const c0 = poly.coeffs[0] ?? Fraction.ZERO;
  const c1 = poly.coeffs[1] ?? Fraction.ZERO;
  if (c0.den !== 1 || c1.den !== 1) return null;
  if (Math.abs(c0.num) > 12 || Math.abs(c1.num) > 12) return null;
  const tiles: Tile[] = [];
  if (c1.num !== 0)
    tiles.push({ kind: 'x', sign: c1.num > 0 ? 1 : -1, multiplicity: Math.abs(c1.num), id: 't-x' });
  if (c0.num !== 0)
    tiles.push({
      kind: 'unit',
      sign: c0.num > 0 ? 1 : -1,
      multiplicity: Math.abs(c0.num),
      id: 't-unit',
    });
  return tiles;
}

/**
 * Format an equation back into a re-parseable ascii string (round-trips
 * through parseEquationInput).
 */
export function equationToString(eq: BalanceEquation): string {
  return `${formatSide(eq.lhs, 'x')} = ${formatSide(eq.rhs, 'x')}`;
}

/**
 * If the equation is exactly `a·x = b` with `a` dividing `b` and |a| > 1,
 * return `a` — the whole-number divisor that isolates x in one step.
 * Otherwise null (nothing to divide by yet).
 */
export function divisorCandidate(eq: BalanceEquation, varName: string): number | null {
  const lhs = Polynomial.fromExpr(canonicalise(foldNumericPowers(eq.lhs)), varName);
  const rhs = Polynomial.fromExpr(canonicalise(foldNumericPowers(eq.rhs)), varName);
  if (!lhs || !rhs || lhs.degree !== 1 || rhs.degree > 0) return null;
  const a = lhs.coeffs[1]!;
  const c0 = lhs.coeffs[0] ?? Fraction.ZERO;
  const b = rhs.coeffs[0] ?? Fraction.ZERO;
  if (c0.num !== 0 || a.den !== 1 || b.den !== 1) return null;
  if (Math.abs(a.num) <= 1) return null;
  if (b.num % a.num !== 0) return null;
  return a.num;
}

/**
 * Generate a random linear equation `a·x + b = c·x + d` with a unique
 * integer solution, guaranteed tileable (small whole-number coefficients).
 */
export function randomLinearEquation(prng: Prng): string {
  const term = (k: number, m: number): string => {
    const xPart = k === 0 ? '' : k === 1 ? 'x' : k === -1 ? '-x' : `${k}x`;
    if (m === 0) return xPart === '' ? '0' : xPart;
    if (xPart === '') return String(m);
    return m > 0 ? `${xPart} + ${m}` : `${xPart} - ${Math.abs(m)}`;
  };
  for (let attempt = 0; attempt < 20; attempt++) {
    const x0 = prng(11) - 5;
    const a = prng(4) + 1;
    const c = prng(a); // 0..a−1 → a ≠ c, unique solution
    const b = prng(15) - 7;
    const d = b + (a - c) * x0;
    if (Math.abs(d) > 12) continue;
    return `${term(a, b)} = ${term(c, d)}`;
  }
  return '2x + 3 = 11';
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  baseEquation: string;
  equation: string;
  trail: { label: string; equation: string }[];
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.equation-balance.label',
    demoId: 'algebra',
    advancedKey: 'equation-balance',

    initialState(): State {
      const defaultPreset = EQUATION_PRESETS[0]!;
      return {
        presetId: defaultPreset.id,
        baseEquation: defaultPreset.equation,
        equation: defaultPreset.equation,
        trail: [],
      };
    },

    applyPreset(state, presetId): State {
      const preset = EQUATION_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return {
        ...state,
        presetId: preset.id,
        baseEquation: preset.equation,
        equation: preset.equation,
        trail: [],
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'alg-balance-scale-svg' });
      host.appendChild(svg);

      const initPreset = EQUATION_PRESETS[0]!;
      const initEq = parseEquationInput(initPreset.equation);
      const balanceScale = createBalanceScale(svg, {
        leftPan: sideToTiles(initEq.lhs, 'x') ?? [],
        rightPan: sideToTiles(initEq.rhs, 'x') ?? [],
        numericXForTilt: 0,
        width: 800,
        height: 460,
      });

      // ── DOM: Stage — headline / solved / fallback text (overlaid on the scale) ──
      const headlineText = createSvgEl('text', {
        x: 400,
        y: 34,
        'text-anchor': 'middle',
        class: 'alg-title',
      });
      svg.appendChild(headlineText);

      const solvedText = createSvgEl('text', {
        x: 400,
        y: 62,
        'text-anchor': 'middle',
        class: 'alg-title',
      });
      svg.appendChild(solvedText);

      const notTileableText = createSvgEl('text', {
        x: 400,
        y: 230,
        'text-anchor': 'middle',
        class: 'alg-text-soft',
      });
      svg.appendChild(notTileableText);

      // ── DOM: Panel — equation input row ──
      const equationInputRow = document.createElement('div');
      equationInputRow.className = 'alg-panel-row';
      const equationLabel = document.createElement('label');
      equationLabel.textContent = ctx.i18n.t('panel.enter-number');
      const equationInput = document.createElement('input');
      equationInput.type = 'text';
      equationInput.className = 'alg-text-input';
      equationInput.addEventListener('change', () => {
        try {
          // Validate by parsing; if valid push the new equation string into state
          parseEquationInput(equationInput.value);
          controls.setState((s) => ({
            ...s,
            baseEquation: equationInput.value,
            equation: equationInput.value,
            trail: [],
          }));
        } catch {
          // On parse error: revert the input to the current state's equation
          controls.setState((s) => s);
        }
      });
      equationInputRow.appendChild(equationLabel);
      equationInputRow.appendChild(equationInput);
      ctx.panel.appendChild(equationInputRow);

      // ── DOM: Panel — op-button row ──
      const opRow = document.createElement('div');
      opRow.className = 'alg-panel-row alg-op-row';

      const pushOp = (op: BalanceOp, label: string): void => {
        controls.setState((s) => {
          try {
            const next = applyOperationToBoth(parseEquationInput(s.equation), op);
            const nextStr = equationToString(next);
            parseEquationInput(nextStr); // round-trip guard
            return { ...s, equation: nextStr, trail: [...s.trail, { label, equation: nextStr }] };
          } catch {
            return s;
          }
        });
      };

      const addOneBtn = document.createElement('button');
      addOneBtn.className = 'alg-op-btn';
      addOneBtn.textContent = '+1';
      addOneBtn.addEventListener('click', () => pushOp({ kind: 'add', value: num(1) }, '+ 1'));
      opRow.appendChild(addOneBtn);

      const subOneBtn = document.createElement('button');
      subOneBtn.className = 'alg-op-btn';
      subOneBtn.textContent = '−1';
      subOneBtn.addEventListener('click', () => pushOp({ kind: 'sub', value: num(1) }, '− 1'));
      opRow.appendChild(subOneBtn);

      const addXBtn = document.createElement('button');
      addXBtn.className = 'alg-op-btn';
      addXBtn.textContent = '+x';
      addXBtn.addEventListener('click', () => pushOp({ kind: 'add', value: variable('x') }, '+ x'));
      opRow.appendChild(addXBtn);

      const subXBtn = document.createElement('button');
      subXBtn.className = 'alg-op-btn';
      subXBtn.textContent = '−x';
      subXBtn.addEventListener('click', () => pushOp({ kind: 'sub', value: variable('x') }, '− x'));
      opRow.appendChild(subXBtn);

      const divideBtn = document.createElement('button');
      divideBtn.className = 'alg-op-btn';
      divideBtn.textContent = '÷';
      divideBtn.addEventListener('click', () => {
        controls.setState((s) => {
          const n = divisorCandidate(parseEquationInput(s.equation), 'x');
          if (n === null) return s;
          try {
            const next = applyOperationToBoth(parseEquationInput(s.equation), {
              kind: 'div',
              value: num(n),
            });
            const nextStr = equationToString(next);
            parseEquationInput(nextStr); // round-trip guard
            return {
              ...s,
              equation: nextStr,
              trail: [...s.trail, { label: `÷ ${n}`, equation: nextStr }],
            };
          } catch {
            return s;
          }
        });
      });
      opRow.appendChild(divideBtn);

      const undoBtn = document.createElement('button');
      undoBtn.className = 'alg-op-btn';
      undoBtn.textContent = ctx.i18n.t('btn.undo');
      undoBtn.addEventListener('click', () => {
        controls.setState((s) => {
          const trail = s.trail.slice(0, -1);
          return { ...s, trail, equation: trail[trail.length - 1]?.equation ?? s.baseEquation };
        });
      });
      opRow.appendChild(undoBtn);

      ctx.panel.appendChild(opRow);

      // ── DOM: Panel — trail list ──
      const trailList = document.createElement('div');
      trailList.className = 'alg-trail';
      ctx.panel.appendChild(trailList);

      // ── DOM: Action bar ──
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);
      actionBar.setActions([
        {
          id: 'reset',
          labelKey: 'btn.reset',
          onClick: () => {
            controls.setState((s) => ({ ...s, equation: s.baseEquation, trail: [] }));
          },
        },
        {
          id: 'randomise',
          labelKey: 'btn.randomise',
          onClick: () => {
            const e = randomLinearEquation(ctx.prng);
            controls.setState((s) => ({ ...s, baseEquation: e, equation: e, trail: [] }));
          },
        },
      ]);

      // ── RenderHandle ──
      const update = (state: State): void => {
        const eq = parseEquationInput(state.equation);
        const leftTiles = sideToTiles(eq.lhs, 'x');
        const rightTiles = sideToTiles(eq.rhs, 'x');

        if (leftTiles !== null && rightTiles !== null) {
          const { solutions } = solveLinear(eq, 'x');
          const numericSolution = solutions.find((sol) => sol.kind === 'num');
          const numericXForTilt = numericSolution ? numericSolution.value.toNumber() : 0;
          balanceScale.update({ leftPan: leftTiles, rightPan: rightTiles, numericXForTilt });
          notTileableText.textContent = '';
        } else {
          balanceScale.update({ leftPan: [], rightPan: [] });
          notTileableText.textContent = ctx.i18n.t('balance.not-tileable');
        }

        // Equation headline
        headlineText.textContent = `${formatSide(eq.lhs, 'x', { useUnicodeSuperscripts: true })} = ${formatSide(eq.rhs, 'x', { useUnicodeSuperscripts: true })}`;

        // Solved state
        solvedText.textContent = isSolved(eq, 'x')
          ? `${ctx.i18n.t('balance.solved')}  x = ${formatSide(eq.rhs, 'x')}`
          : '';

        // Sync equation input — guard caret
        if (equationInput.value !== state.equation) {
          equationInput.value = state.equation;
        }

        // Sync the divide button
        const divisor = divisorCandidate(eq, 'x');
        divideBtn.textContent = `÷${divisor ?? ''}`;
        divideBtn.disabled = divisor === null;

        // Rebuild the trail list
        trailList.replaceChildren();
        for (const entry of state.trail) {
          const row = document.createElement('div');
          row.textContent = `${entry.label}  →  ${entry.equation}`;
          trailList.appendChild(row);
        }
      };

      return {
        update,
        dispose() {
          balanceScale.destroy();
          actionBar.clear();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
